import { queryOptions } from "@tanstack/react-query"
import {
  type GachaApiOptions,
  fetchGachaJson,
  postGachaJson,
} from "./gacha-api"
import type { GachaSolanaCluster } from "./gacha-solana-config"

export const GACHA_DISCLAIMER_VERSION = 1

export type DisclaimerNetwork = "mainnet" | "testnet"

export type DisclaimerAcceptanceSummary = {
  privyId: string
  disclaimerVersion: number
  network: DisclaimerNetwork
  createdAt: string
}

export type CheckDisclaimerAcceptanceResponse =
  | {
      accepted: false
    }
  | {
      accepted: true
      acceptance: DisclaimerAcceptanceSummary
    }

export type AcceptDisclaimerRequest = {
  walletAddress: string
  disclaimerVersion: number
  network: DisclaimerNetwork
}

export type AcceptDisclaimerResponse = {
  privyId: string
  disclaimerVersion: number
  network: DisclaimerNetwork
  accepted: true
  createdAt: string
}

type AuthenticatedDisclaimerRequest = {
  accessToken: string
}

type GetAccessToken = () => Promise<string | null>

export function getGachaDisclaimerNetwork(
  cluster: GachaSolanaCluster,
): DisclaimerNetwork {
  return cluster === "mainnet" ? "mainnet" : "testnet"
}

export function getGachaDisclaimerAcceptanceQueryKey({
  privyId,
  disclaimerVersion,
  network,
}: {
  privyId: string
  disclaimerVersion: number
  network: DisclaimerNetwork
}) {
  return [
    "gacha-disclaimer-acceptance",
    privyId,
    disclaimerVersion,
    network,
  ] as const
}

export function getGachaDisclaimerAcceptanceUserQueryKey(privyId: string) {
  return ["gacha-disclaimer-acceptance", privyId] as const
}

function requireAccessToken(accessToken: string | null) {
  if (!accessToken) {
    throw new Error("Privy access token is unavailable")
  }
  return accessToken
}

function getAuthorizationHeaders(accessToken: string) {
  return {
    Authorization: `Bearer ${accessToken}`,
  }
}

export function checkGachaDisclaimerAcceptance(
  {
    accessToken,
    disclaimerVersion,
    network,
  }: AuthenticatedDisclaimerRequest & {
    disclaimerVersion: number
    network: DisclaimerNetwork
  },
  options?: GachaApiOptions,
) {
  const query = new URLSearchParams({
    disclaimerVersion: String(disclaimerVersion),
    network,
  })

  return fetchGachaJson<CheckDisclaimerAcceptanceResponse>(
    `/disclaimer-acceptance?${query.toString()}`,
    {
      headers: getAuthorizationHeaders(accessToken),
    },
    options,
  )
}

export function acceptGachaDisclaimer(
  {
    accessToken,
    ...request
  }: AuthenticatedDisclaimerRequest & AcceptDisclaimerRequest,
  options?: GachaApiOptions,
) {
  return postGachaJson<AcceptDisclaimerResponse>(
    "/disclaimer-acceptance",
    request,
    options,
    getAuthorizationHeaders(accessToken),
  )
}

export function createGachaDisclaimerAcceptanceQueryOptions({
  enabled,
  privyId,
  disclaimerVersion,
  network,
  getAccessToken,
  options,
}: {
  enabled: boolean
  privyId: string
  disclaimerVersion: number
  network: DisclaimerNetwork
  getAccessToken: GetAccessToken
  options?: GachaApiOptions
}) {
  return queryOptions({
    queryKey: getGachaDisclaimerAcceptanceQueryKey({
      privyId,
      disclaimerVersion,
      network,
    }),
    queryFn: async () =>
      checkGachaDisclaimerAcceptance(
        {
          accessToken: requireAccessToken(await getAccessToken()),
          disclaimerVersion,
          network,
        },
        options,
      ),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  })
}

export function toAcceptedDisclaimerCheckResponse(
  response: AcceptDisclaimerResponse,
): CheckDisclaimerAcceptanceResponse {
  return {
    accepted: true,
    acceptance: {
      privyId: response.privyId,
      disclaimerVersion: response.disclaimerVersion,
      network: response.network,
      createdAt: response.createdAt,
    },
  }
}
